import { Component, computed, input, output, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Spinner } from '../spinner/spinner';
import { DocumentItem, TableItem } from '../interfaces';

export interface ExtendedTableItem extends TableItem {
  isIndexFolder?: boolean;
}

@Component({
  selector: 'app-document-table',
  standalone: true, 
  imports: [CommonModule, DatePipe, Spinner],
  templateUrl: './document-table-component.html',
  styleUrl: './document-table-component.scss',
})
export class DocumentTableComponent {
  subtitle = input.required<string>();
  documents = input.required<DocumentItem[]>();
  isLoading = input<boolean>(false);
  loadingMessage = input<string>('');
  hasError = input<boolean>(false);
  showIngestButton = input<boolean>(false);
  isProcessing = input<boolean>(false);
  basePath = input<string>('input/');
  showInfoAction = input<boolean>(false);
  ingestEnabled = input<boolean>(true);
  showSelection = input<boolean>(false);

  selectionChanged = output<DocumentItem[]>();
  pathChanged = output<string>();
  ingestClicked = output<void>();
  infoActionClicked = output<DocumentItem>();

  private readonly _currentPath = signal<string | null>(null);
  selectedPaths = signal<Set<string>>(new Set());

  currentPath = computed(() => this._currentPath() ?? this.basePath());
  pathSegments = computed(() => this.currentPath().split('/').filter((s: string) => s.length > 0));
  isRootLevel = computed(() => this.pathSegments().length === 1);
  isMultiSelectEnabled = computed(() => this.showSelection() && this.pathSegments().length >= 2);

  // --- LOGICA BREADCRUMB ---
  breadcrumb = computed(() => {
    const path = this.currentPath();
    const relative = path.startsWith(this.basePath()) ? path.substring(this.basePath().length) : path;
    if (!relative) return [];
    
    const segments = relative.split('/').filter(s => s.length > 0);
    const crumbs: { name: string, fullPath: string }[] = [];
    let acc = this.basePath();
    
    for (const seg of segments) {
      acc += seg + '/';
      crumbs.push({ name: seg, fullPath: acc });
    }
    return crumbs;
  });

  // --- LOGICA TABELLA VISIBILE ---
  visibleItems = computed(() => {
    const allDocs = this.documents() || [];
    const path = this.currentPath();
    const isRoot = this.isRootLevel();
    const items: ExtendedTableItem[] = [];
    const folders = new Set<string>();

    for (const doc of allDocs) {
      if (doc.percorso?.startsWith(path)) {
        const relPath = doc.percorso.substring(path.length);
        if (relPath === '') continue;

        const slashIndex = relPath.indexOf('/');
        if (slashIndex !== -1) {
          const folderName = relPath.substring(0, slashIndex);
          if (!folders.has(folderName)) {
            folders.add(folderName);
            items.push({
              isFolder: true,
              isIndexFolder: isRoot,
              name: folderName,
              fullPath: path + folderName + '/',
              document: null
            });
          }
        } else {
          items.push({
            isFolder: false,
            isIndexFolder: false,
            name: doc.nome,
            fullPath: doc.percorso,
            document: doc
          });
        }
      }
    }

    return items.sort((a, b) => {
      if (a.isFolder && !b.isFolder) return -1;
      if (!a.isFolder && b.isFolder) return 1;
      return a.name.localeCompare(b.name);
    });
  });

  // --- LOGICA DI SELEZIONE ---
  private getDocumentsUnderPath(path: string): DocumentItem[] {
    return this.documents().filter((d: DocumentItem) => d.percorso.startsWith(path) && d.percorso !== path);
  }

  private getAllVisibleDocuments(): DocumentItem[] {
    const items = this.visibleItems();
    const docs: DocumentItem[] = [];
    for (const item of items) {
      if (item.isFolder) {
        docs.push(...this.getDocumentsUnderPath(item.fullPath));
      } else if (item.document) {
        docs.push(item.document);
      }
    }
    return docs;
  }

  isItemChecked(item: ExtendedTableItem): boolean {
    if (!item.isFolder) return this.selectedPaths().has(item.fullPath);
    const children = this.getDocumentsUnderPath(item.fullPath);
    if (children.length === 0) return false;
    return children.every((c: DocumentItem) => this.selectedPaths().has(c.percorso));
  }

  isItemIndeterminate(item: ExtendedTableItem): boolean {
    if (!item.isFolder) return false;
    const children = this.getDocumentsUnderPath(item.fullPath);
    if (children.length === 0) return false;
    const selectedCount = children.filter((c: DocumentItem) => this.selectedPaths().has(c.percorso)).length;
    return selectedCount > 0 && selectedCount < children.length;
  }

  toggleItem(item: ExtendedTableItem) {
    const newSet = new Set(this.selectedPaths());
    if (item.isFolder) {
      const children = this.getDocumentsUnderPath(item.fullPath);
      const isAllChecked = children.every((c: DocumentItem) => newSet.has(c.percorso));
      if (isAllChecked) {
        children.forEach((c: DocumentItem) => newSet.delete(c.percorso));
      } else {
        children.forEach((c: DocumentItem) => newSet.add(c.percorso));
      }
    } else if (newSet.has(item.fullPath)) {
      newSet.delete(item.fullPath);
    } else {
      newSet.add(item.fullPath);
    }

    this.selectedPaths.set(newSet);
    this.emitSelection();
  }

  isAllSelected = computed(() => {
    const docs = this.getAllVisibleDocuments();
    if (docs.length === 0) return false;
    return docs.every((d: DocumentItem) => this.selectedPaths().has(d.percorso));
  });

  isAllIndeterminate = computed(() => {
    const docs = this.getAllVisibleDocuments();
    if (docs.length === 0) return false;
    const selectedCount = docs.filter((d: DocumentItem) => this.selectedPaths().has(d.percorso)).length;
    return selectedCount > 0 && selectedCount < docs.length;
  });

  toggleAll() {
    const docs = this.getAllVisibleDocuments();
    const newSet = new Set(this.selectedPaths());
    if (this.isAllSelected()) {
      docs.forEach((d: DocumentItem) => newSet.delete(d.percorso));
    } else {
      docs.forEach((d: DocumentItem) => newSet.add(d.percorso));
    }
    this.selectedPaths.set(newSet);
    this.emitSelection();
  }

  private emitSelection() {
    const selectedDocs = this.documents().filter((d: DocumentItem) => this.selectedPaths().has(d.percorso));
    this.selectionChanged.emit(selectedDocs);
  }

  // --- NAVIGAZIONE ---
  goToFolder(path: string) {
    // FIX: Se si torna alla radice tramite breadcrumb, resetta la selezione
    if (path === this.basePath()) {
      this.selectedPaths.set(new Set());
      this.emitSelection();
    }
    this._currentPath.set(path);
    this.pathChanged.emit(path);
  }

  goBack() {
    const path = this.currentPath();
    if (path === this.basePath()) return;

    const withoutTrailing = path.slice(0, -1);
    const lastSlash = withoutTrailing.lastIndexOf('/');
    const newPath = lastSlash === -1 ? this.basePath() : withoutTrailing.substring(0, lastSlash + 1);
    
    if (newPath === this.basePath()) {
      this.selectedPaths.set(new Set());
      this.emitSelection();
    }

    this._currentPath.set(newPath);
    this.pathChanged.emit(newPath);
  }
}